/**
 * Tag vocabulary helpers, shared by the public grid's tag filter and the
 * gallery editor's tag input.
 *
 * Gallery tags are free text (a `String[]` column, no Tag table), so the only
 * thing standing between the vocabulary and three spellings of the same idea is
 * how tags get entered. Everything here works on a *folded* key — case- and
 * accent-insensitive — so "Zürich", "zurich" and "ZURICH" are one tag, and the
 * spelling that already exists is the one that wins.
 */

/** Case- and accent-insensitive key, so "zurich" matches "Zürich". */
export const foldTag = (s) =>
  String(s)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/** Trim and collapse inner whitespace — "  wide   angle " → "wide angle". */
export const cleanTag = (s) => String(s ?? "").trim().replace(/\s+/g, " ");

/** Split typed or pasted text on commas into clean, non-empty tags. */
export const splitTags = (text) => String(text ?? "").split(",").map(cleanTag).filter(Boolean);

/** Drop fold-duplicates from a list, keeping the first spelling seen. */
export function uniqueTags(tags) {
  const seen = new Set();
  const out = [];
  for (const raw of tags) {
    const tag = cleanTag(raw);
    if (!tag) continue;
    const key = foldTag(tag);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

/**
 * The vocabulary entry a typed value refers to, or null if it is new.
 *
 * Matching is on the folded *key* and on every display name the entry carries,
 * so typing the German name of a tag finds it even though galleries store the
 * key. Entries are `{ key, label, count, folded: string[] }` (see
 * tagManager.vocabulary); a bare string or `{ key }` works too.
 */
export function matchTag(tag, vocabulary = []) {
  const key = foldTag(cleanTag(tag));
  if (!key) return null;
  return (
    vocabulary.find((entry) => {
      const folded = entry.folded || [foldTag(entry.key ?? entry)];
      return folded.includes(key);
    }) || null
  );
}

/**
 * The key this tag should be stored under: an existing entry's key when the
 * input names one in either language, or the cleaned input if it is genuinely
 * new. This is what keeps "landscape" from joining "Landscape" in the
 * vocabulary — and what lets the admin type "Landschaft" and still store the
 * key the rest of the data uses.
 */
export function canonicalTag(tag, vocabulary = []) {
  const clean = cleanTag(tag);
  if (!clean) return "";
  const known = matchTag(clean, vocabulary);
  return known ? known.key ?? known : clean;
}
